import { useState, useEffect, MouseEvent, useCallback } from 'react';
import { connect } from 'react-redux';

import { HEADER_STICKED_HEIGHT } from 'CorePackage/HeaderModule/constants/headerStickedHeight';
import { isElementVisible, revealCollapsedElement } from 'CorePackage/MainModule/common/revealCollapsedElement';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getAnnounceHeight } from 'CorePackage/MainModule/reducers/coreReducer';
import { IErrorFieldWithProps } from 'KnmPackage/MainModule/interfaces/IErrorFieldWithProps';
import { getKnmErrorFields } from 'KnmPackage/MainModule/selectors/knmSelectors';

import styles from './ErrorsIcon.module.scss';

export interface IErrorsIconProps {
  className?: string;
  errorFields: Array<IErrorFieldWithProps> | undefined;
  autoHidingTooltip?: 'onHeaderStickOn' | 'onHeaderStickOff';
  announceHeight: number | undefined;
}

export const ErrorsIconWithoutStore = ({
  className,
  errorFields,
  autoHidingTooltip,
  announceHeight,
}: IErrorsIconProps) => {
  const [isErrorsTooltipVisible, setIsErrorsTooltipVisible] = useState(false);

  const onScroll = () => {
    const windowOffset = window.scrollY;
    const headerElement = document.querySelector('header');

    if (headerElement) {
      if (
        (windowOffset > headerElement.offsetHeight && autoHidingTooltip === 'onHeaderStickOn') ||
        (windowOffset <= headerElement.offsetHeight && autoHidingTooltip === 'onHeaderStickOff')
      ) {
        setIsErrorsTooltipVisible(false);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  });

  const handleErrorsIconClick = useCallback(() => setIsErrorsTooltipVisible(prev => !prev), []);
  const makeErrorsTooltipLinkClickHandler = () => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsErrorsTooltipVisible(false);
    const scrollElementId = e.currentTarget.hash.replace('#', '');

    // Поле может лежать в свёрнутом аккордеоне - сначала просим его раскрыться,
    // иначе элемента либо нет в DOM, либо он скрыт и скроллить будет некуда
    revealCollapsedElement(scrollElementId).then(element => {
      let scrollElement: HTMLElement | null = element;

      // Если раскрыть не удалось, поднимаемся до ближайшего видимого предка
      while (scrollElement && !isElementVisible(scrollElement)) {
        scrollElement = scrollElement.parentElement;
      }

      if (!scrollElement) return;

      window.scrollTo({
        top:
          scrollElement.getBoundingClientRect().top +
          window.pageYOffset -
          HEADER_STICKED_HEIGHT -
          (announceHeight || 0) -
          2,
        behavior: 'smooth',
      });
    });
  };

  if (!errorFields?.length) {
    return null;
  }

  const errorsTooltipAttributes = {
    ...(isErrorsTooltipVisible && { 'data-visible': true }),
  };

  return (
    <div className={className ? `${styles.Container} ${className}` : styles.Container}>
      <span className={styles.Icon} onClick={handleErrorsIconClick} />
      <div className={styles.ErrorsTooltip} {...errorsTooltipAttributes}>
        <div className={styles.ErrorsTooltipTitle}>Неверно заполнены поля:</div>
        <ul className={styles.ErrorsTooltipList}>
          {errorFields.map(errorField => (
            <li key={errorField.flatName}>
              <a
                className={styles.ErrorsTooltipLink}
                href={`#${errorField.scrollElementId}`}
                onClick={makeErrorsTooltipLinkClickHandler()}
              >
                {errorField.title}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  announceHeight: getAnnounceHeight(state),
  errorFields: getKnmErrorFields(state),
});

export const ErrorsIcon = connect(mapStateToProps)(ErrorsIconWithoutStore);
